import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  getShoplists,
  createShoplist,
  toggleProductInShoplist,
} from "../services/shoplist.services";

function ShoplistSelectModal({ isOpen, onClose, product, onSaved }) {
  const { t } = useTranslation();
  const [shoplists, setShoplists] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchLists = async () => {
    if (!product?._id) return;
    try {
      setLoading(true);
      const res = await getShoplists(product._id);
      if (res?.success) {
        const list = res.data || [];
        setShoplists(list);
        // Pre-check any lists that already contain this product
        const initialChecked = list.filter((item) => item.hasProduct).map((item) => item._id);
        setSelectedIds(initialChecked);
      }
    } catch (err) {
      console.error("Failed to load shoplists:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && product?._id) {
      setNewTitle("");
      fetchLists();
    }
  }, [isOpen, product?._id]);

  if (!isOpen || !product) return null;

  const handleToggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleCreateNew = async (e) => {
    e?.preventDefault();
    if (!newTitle.trim()) return;

    try {
      setCreating(true);
      const res = await createShoplist({ title: newTitle.trim() });
      if (res?.success && res.data) {
        const created = res.data;
        // Add to local shoplists and automatically check it
        setShoplists((prev) => [...prev, { ...created, totalProducts: 0, hasProduct: false }]);
        setSelectedIds((prev) => [...prev, created._id]);
        setNewTitle("");
        Swal.fire({
          icon: "success",
          text: t("shoplist.created", { defaultValue: "Shoplist created!" }),
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
      }
    } catch (err) {
      console.error("Error creating shoplist:", err);
      Swal.fire({
        icon: "error",
        text: err?.response?.data?.message || "Failed to create shoplist",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await toggleProductInShoplist({
        productId: product._id,
        shoplistIds: selectedIds,
      });

      if (res?.success) {
        Swal.fire({
          icon: "success",
          text: res.message || t("shoplist.saved", { defaultValue: "Saved to Shoplist!" }),
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2500,
        });
        if (onSaved) {
          onSaved(selectedIds);
        }
        onClose();
      }
    } catch (err) {
      console.error("Error saving product to shoplists:", err);
      Swal.fire({
        icon: "error",
        text: err?.response?.data?.message || "Failed to save product",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 transition-all transform scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="m11.645 20.91-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z" />
              </svg>
            </span>
            <h3 className="font-bold text-gray-900 text-lg">
              {t("shoplist.save_to_shoplist", { defaultValue: "Save to Shoplist" })}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Product Preview */}
        <div className="px-6 py-3 bg-pink-50/40 border-b border-pink-100/60 flex items-center gap-3">
          {product.image || product.compressed_image ? (
            <img
              src={product.compressed_image || product.image}
              alt={product.title}
              className="w-12 h-12 rounded-lg object-cover border border-gray-200 bg-white"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center text-gray-400 font-bold text-xs">
              IMG
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-800 truncate">{product.title}</h4>
            <p className="text-xs text-pink-600 font-bold">
              {product.isAskForPrice ? t("ask_to_seller", "Ask to Seller") : product.price ? `INR ${product.price}` : ""}
            </p>
          </div>
        </div>

        {/* Body: List of Shoplists */}
        <div className="px-6 py-4 max-h-64 overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : shoplists.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">
              <p className="font-semibold text-gray-700 mb-1">
                {t("shoplist.no_shoplists_yet", { defaultValue: "No shoplists found" })}
              </p>
              <p className="text-xs text-gray-400">
                {t("shoplist.create_first_instruction", { defaultValue: "Create your first shoplist below to organize your favorites!" })}
              </p>
            </div>
          ) : (
            shoplists.map((list) => {
              const isChecked = selectedIds.includes(list._id);
              return (
                <label
                  key={list._id}
                  onClick={() => handleToggle(list._id)}
                  className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer select-none ${
                    isChecked
                      ? "border-pink-500 bg-pink-50/50 shadow-sm"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {}} // Handled by container onClick
                      className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500 cursor-pointer accent-pink-500"
                    />
                    <span className="text-sm font-semibold text-gray-800">{list.title}</span>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
                    {list.totalProducts || 0} {t("shoplist.items", { defaultValue: "items" })}
                  </span>
                </label>
              );
            })
          )}
        </div>

        {/* Create New Shoplist Form */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/60">
          <form onSubmit={handleCreateNew} className="flex gap-2">
            <input
              type="text"
              placeholder={t("shoplist.new_title_placeholder", { defaultValue: "New shoplist title..." })}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="flex-1 text-sm px-3.5 py-2 rounded-lg border border-gray-300 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition"
              maxLength={40}
            />
            <button
              type="submit"
              disabled={creating || !newTitle.trim()}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-pink-50 text-pink-600 hover:bg-pink-100 disabled:opacity-50 transition shrink-0"
            >
              {creating ? t("common.creating", "Adding...") : t("shoplist.add_new", "+ Create")}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 text-sm font-semibold text-white bg-[var(--secondary-500,#FF63B5)] hover:opacity-95 rounded-lg shadow-sm hover:shadow transition disabled:opacity-50"
          >
            {saving ? t("common.saving", "Saving...") : t("shoplist.done", { defaultValue: "Done" })}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShoplistSelectModal;
